import { Brand, setupBrandNameProperties } from 'AppBranding';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { getActiveInstitution, getCohort, getHasPendingProgramApplication } from 'Users';
import { getProgramFamilyFormDatum } from 'ProgramFamilyFormData';
import { ProgramFamily } from 'Program';
import transformKeyCase from 'Utils/transformKeyCase';
import { navigationHelper } from 'NavigationHelpers';
import {
    buildFormCompletionInput,
    getFormsCompletePercent,
    getStepValidationErrors,
    markStepVisited,
} from 'FormCompletionHelper';
import { CareerProfileFormContext, IS_NOT_MOSTLY_CJK_TEST } from 'FormConfigs';
import template from '../views/edit_career_profile.html';
import angularModule from './careers_module';
import { determineTargetProgramType } from '../../determineTargetProgramType';
import { determineProgramPreferencesStateKey } from '../../determineProgramPreferencesStateKey';

const templateUrl = cacheAngularTemplate(angularModule, template);

// Every step form gets this attribute list, and each step form directive declares only the bindings it
// actually consumes in its own `scope` block. Angular ignores an attribute a directive does not declare,
// so the `scope` block is the real contract.
//
// The step forms used to share this directive's scope, which made every property below an undeclared
// global. Read a step form's `scope` block to see what it depends on.
//
// NOTE: the object bindings are one-way (`<`). A one-way binding still shares the object reference, so
// the property changes a step form makes reach this directive. Reassigning the whole object here also
// propagates down. Only a step form reassigning its own binding would fail to propagate up.
const STEP_FORM_ATTRS = [
    // angular-multi-step-form adds $setValidity to the scope it creates for each step. Its
    // `form-step-validity` directive, which every step template puts on its <form>, then reads
    // `scope.$setValidity`. The step forms used to inherit it prototypally. Each one binds it by that
    // exact name now, because the directive looks it up rather than taking it as an argument.
    'set-validity="$setValidity"',
    'career-profile="careerProfile"',
    'program-family-form-datum="programFamilyFormDatum"',
    'current-user="currentUser"',
    'current-form="currentForm"',
    'form-context="formContext"',
    'program-type="programType"',
    'steps="steps"',
    'steps-progress-map="stepsProgressMap"',
    'gdpr-applies-to-user="gdprAppliesToUser"',
    'disable-substantial-application-edits="disableSubstantialApplicationEdits"',
    'transient-form-values="transientFormValues"',
    'go-to-step="goToStep({$$index: $$index})"',
    'on-save="save()"',
    'on-save-and-next="saveAndNext()"',
    'set-disable-save-for-step="setDisableSaveForStep(stepName, shouldDisable)"',
    'set-save-click-interceptor-for-step="setSaveClickInterceptorForStep(stepName, interceptor)"',
    'set-save-next-click-interceptor-for-step="setSaveNextClickInterceptorForStep(stepName, interceptor)"',
].join(' ');

angularModule.directive('editCareerProfile', [
    '$injector',
    function factory($injector) {
        const $rootScope = $injector.get('$rootScope');
        const TranslationHelper = $injector.get('TranslationHelper');
        const AppHeaderViewModel = $injector.get('Navigation.AppHeader.AppHeaderViewModel');
        const DialogModal = $injector.get('DialogModal');
        const scopeTimeout = $injector.get('scopeTimeout');
        const SiteMetadata = $injector.get('SiteMetadata');
        const NavigationHelperMixin = $injector.get('Navigation.NavigationHelperMixin');
        const scrollHelper = $injector.get('scrollHelper');
        const reactRouterBridge = $injector.get('reactRouterBridge');
        const CareerProfile = $injector.get('CareerProfile');
        const EditCareerProfileHelper = $injector.get('EditCareerProfileHelper');
        const ConfigFactory = $injector.get('ConfigFactory');
        const FixViewportOnBlur = $injector.get('FixViewportOnBlur');
        const $q = $injector.get('$q');
        const Cohort = $injector.get('Cohort');
        const $translate = $injector.get('$translate');
        const { loadRoute } = navigationHelper();

        return {
            restrict: 'E',
            scope: {
                // A CareerProfileFormContext. See the isApplication/isStudentProfile booleans below.
                //
                // Pass a literal, not a scope property. This directive reads the value during link to
                // decide which watchers to register, and settings_dir.js has an `async link`, so a scope
                // property it sets is not available yet when this directive links.
                formContext: '<',
                steps: '<',
                initialStep: '<?',
                user: '<?', // the user whose profile to edit (defaults to currentUser if not provided),
                stepsProgressMap: '<?',
                goToStep: '&?',
                programType: '=?',
            },
            templateUrl,

            link(scope, elem) {
                if (!scope.formContext) {
                    throw new Error('edit-career-profile requires a form-context.');
                }

                // Which form the user is filling out. These used to be methods on EditCareerProfileHelper
                // that compared window.location.pathname against 3 hard-coded route strings. Every
                // component in this tree could call them, so the mode was ambient global state. Now the
                // caller passes it in, and these booleans are the declared contract for the step forms.
                scope.isApplication = scope.formContext === CareerProfileFormContext.application;
                scope.isStudentProfile = scope.formContext === CareerProfileFormContext.studentProfile;

                scope.disabledSaveSteps = {};
                scope.saveClickInterceptors = {};
                scope.saveNextClickInterceptors = {};

                // Values that belong to the form but to neither persisted model. A step form writes
                // here for an input it renders that has no column of its own, and getSchemaFieldErrors
                // merges them over the model values.
                //
                // A step form is free to leave this empty. Every schema rule that reads one of these
                // keys passes when the key is absent, which is the right answer for saved values. Each
                // rule documents why its value cannot come from a model. See FormConfigs.
                scope.transientFormValues = {};

                scope.setDisableSaveForStep = (stepName, shouldDisable) => {
                    scope.disabledSaveSteps = { ...scope.disabledSaveSteps, [stepName]: shouldDisable };
                };

                scope.setSaveClickInterceptorForStep = (stepName, saveClickInterceptor) => {
                    scope.saveClickInterceptors = { ...scope.saveClickInterceptors, [stepName]: saveClickInterceptor };
                };

                scope.setSaveNextClickInterceptorForStep = (stepName, saveClickInterceptor) => {
                    scope.saveNextClickInterceptors = {
                        ...scope.saveNextClickInterceptors,
                        [stepName]: saveClickInterceptor,
                    };
                };

                scope.currentStepName = () => {
                    const currentPage = getCurrentPage();
                    const currentStep = scope.steps[currentPage - 1];
                    return currentStep.stepName;
                };

                scope.currentStepIsSaveDisabled = () => !!scope.disabledSaveSteps[scope.currentStepName()];

                NavigationHelperMixin.onLink(scope);
                scope.proxy = {};
                const translationHelper = new TranslationHelper('careers.edit_career_profile');

                AppHeaderViewModel.setBodyBackground();
                AppHeaderViewModel.showAlternateHomeButton = false;

                // The prose in several error labels interpolates `brandName`, which is the standard name
                // for Valar and the long name everywhere else.
                // application_questions_form_dir.js derives it the same way for its own template.
                Object.defineProperty(scope, 'brandName', {
                    get() {
                        return Cohort.branding(scope.programType) === Brand.valar
                            ? scope.brandNameStandard
                            : scope.brandNameLong;
                    },
                    configurable: true,
                });

                setupBrandNameProperties($injector, scope, {
                    branding: () => scope.currentUser && Cohort.branding(scope.programType),
                });

                scrollHelper.scrollToTop();

                // Default title
                SiteMetadata.updateHeaderMetadata();
                ConfigFactory.getConfig().then(config => {
                    scope.gdprAppliesToUser = config.gdprAppliesToUser();
                });

                //-----------------------------
                // Profile Data
                //-----------------------------

                Object.defineProperty(scope, 'currentUser', {
                    get() {
                        if (scope.user) return scope.user;
                        return $rootScope.currentUser;
                    },
                });

                // scope.careerProfile is a copy of the currentUser's career_profile and acts as a proxy,
                // allowing us to easily throw away changes if the user dirties the form and then navigates away
                // to another step without mucking up the career_profile on the currentUser.
                const getCareerProfileProxy = () => CareerProfile.new(scope.currentUser.career_profile.asJson());

                // Similar to scope.careerProfile, scope.programFamilyFormDatum acts as a proxy. We can update
                // the proxy without mutating anything on $rootScope.currentUser, and can easily throw away changes
                // if the user abandons their updates.
                const getProgramFamilyFormDatumProxy = () => {
                    const programFamily = scope.currentUser.relevantCohort.programFamily;
                    const programFamilyFormDatum = getProgramFamilyFormDatum(scope.currentUser, programFamily) || {
                        programFamily,
                    };

                    // Note that we use the JSON.parse(JSON.stringify(...)) trick here to create a DEEP copy
                    // of the programFamilyFormDatum. We can't use a SHALLOW copy here since it would share
                    // the same references as the source.
                    // Ultimately, I'd like to use `structuredClone()` here but it's not supported by all browsers in
                    // our browserslist. Potential optimization in the future.
                    // See also https://developer.mozilla.org/en-US/docs/Web/API/structuredClone.
                    return JSON.parse(JSON.stringify(programFamilyFormDatum));
                };

                // NOTE: Since this $watch on the currentUser only triggers if the REFERENCE to currentUser changes,
                // these lines should theoretically only run once, which is when this directive first renders.
                scope.$watch('currentUser', () => {
                    if (scope.currentUser) {
                        scope.careerProfile = getCareerProfileProxy();
                        scope.programFamilyFormDatum = getProgramFamilyFormDatumProxy();
                        scope.$watch('steps', setupFormSteps);
                    }
                });

                if (scope.isApplication) {
                    scope.programType = scope.currentUser.relevantCohort.programType;
                    scope.disableSubstantialApplicationEdits = false;

                    // We recompute programType from the program-preferences state key, which already
                    // folds in every input that can change it: survey_years_full_time_experience,
                    // survey_highest_level_completed_education_description, age/birthdate, and the
                    // applicant's completed degree experiences (degree level + graduation; see
                    // determineProgramPreferencesStateKey). education_experiences in particular can't be
                    // watched via a simple scope path, so watching the derived key is what keeps programType
                    // fresh when the Formal Degree section is edited. Without this, programType stays stale
                    // (null) after the applicant completes their degree, hiding the Submit button until a
                    // full page refresh.
                    //
                    // NOTE: the inputs to this state key are the "substantial" application fields; if they
                    // change, you probably want to change references to disableSubstantialApplicationEdits
                    // (see basic_info_form.html).
                    scope.$watch(
                        () =>
                            scope.careerProfile ? determineProgramPreferencesStateKey(scope.careerProfile) : undefined,
                        () => {
                            // The master_of_business_administration program family is currently the only degree-granting
                            // program family we have that offers more than one program. When a user fills out an application
                            // for this program family, they're really applying for all of the programs at the same time.
                            // Based on the answers they provide to the fields that feed the program-preferences state key,
                            // the program_type powering the application may need to be changed. The program(s)
                            // they'll be considered for will be communicated to the user on the Program Preferences
                            // form. If the applicant doesn't have the necessary requirements to be placed into
                            // a program, we set program_type to null so that the Program Preferences form will
                            // be invalid and prevent the applicant from submitting the application.
                            if (
                                scope.currentUser.relevantCohort.programFamily ===
                                ProgramFamily.master_of_business_administration
                            ) {
                                // Our recommendation based on the answers to the questions that feed the watched state key above.
                                //
                                // Read the explanation above the condition wrapping this logic, but I wanted to add a note here
                                // that this is confusing. We are implicitly relying on determineTargetProgramType to return null here
                                // if we don't find a program type to recommend because we didn't match up a program preferences state
                                // key with the Cohort's ProgramTypeConfig's programPreferenceStateKeys (meaning the user does not meet
                                // the requirements of the program).
                                //
                                // When determineTargetProgramType returns null, that has the side effect of disabling the Submit Application
                                // form because we won't lookup a Cohort record to apply to via scope.program_type. This is only relevant to the
                                // master_of_business_administration program family because the Cohort one is applying to changes based on one's
                                // answers to the properties that feed the watched state key above. All other program families have one promoted Cohort at a given time
                                // and will have scope.programType and scope.cohort in the Submit Application form.
                                //
                                // But also know that Submit Application form submission can be disabled simply by the program preferences state
                                // having `saveButtonsDisabled: true`.
                                //
                                // I would LOVE to get rid of this and rely on the Submit Application Form's handling of `saveButtonsDisabled: true` to
                                // determine when to block application submission, BUT we'd also have to get rid of our program_type recommendation for
                                // applications to the master_of_business_administration program family in the process, which is a harder sell.
                                scope.programType = determineTargetProgramType(
                                    determineProgramPreferencesStateKey(scope.careerProfile),
                                    getActiveInstitution(scope.currentUser)?.id,
                                );
                            }
                        },
                    );

                    scope.$watch('currentUser', () => {
                        if (!scope.currentUser) return;

                        // If the user edits their account name or nickname, we want the career profile proxy to reflect that.
                        if (scope.careerProfile.name !== scope.currentUser.name) {
                            scope.careerProfile.name = scope.currentUser.name;
                        }

                        if (scope.careerProfile.nickname !== scope.currentUser.nickname) {
                            scope.careerProfile.nickname = scope.currentUser.nickname;
                        }
                    });

                    // We don't allow certain things in the Application to be changed after submission
                    if (getHasPendingProgramApplication(scope.currentUser)) {
                        scope.disableSubstantialApplicationEdits = true;
                    }
                }

                // NOTE: In the Profile, we know the programType immediately because the user has already submitted an application.
                if (scope.isStudentProfile) {
                    scope.programType = getCohort(scope.currentUser).programType;
                    scope.disableSubstantialApplicationEdits = false;
                }

                //-----------------------------
                // Multi-Step Form Config
                //-----------------------------

                function getCurrentPage() {
                    return parseInt(reactRouterBridge.search().page, 10) || 1;
                }

                function setupFormSteps(newValue, oldValue) {
                    if (
                        !scope.careerProfile ||
                        !scope.steps ||
                        (scope.stepsInfo && JSON.stringify(newValue) === JSON.stringify(oldValue))
                    ) {
                        return;
                    }

                    // it's important to re-build steps in an async manner such that the multi-form-container is re-initialized
                    scope.stepsInfo = undefined;
                    scopeTimeout(scope, () => {
                        scope.stepsInfo = scope.steps
                            .map(step => step.stepName)
                            .map(section => {
                                // return a template that passes along necessary model info
                                const directiveName = `${section.replace(/_/g, '-').snakeCase('-')}-form`;
                                const templateStr = `<${directiveName} ${STEP_FORM_ATTRS}></${directiveName}>`;
                                const title = translationHelper.get(section);

                                return {
                                    key: section,
                                    template: templateStr,
                                    title,
                                    hasForm: true,
                                    data: { stepName: section },
                                };
                            });
                    });
                }

                //-----------------------------
                // Step Navigation
                //-----------------------------

                // see also: form_helper.js::supportForm
                const formListener = scope.$on('formHelperCurrentForm', (_evt, formController) => {
                    scope.currentForm = formController;
                });
                scope.$on('$destroy', formListener);

                function scrollToContentTop() {
                    scopeTimeout(scope, () => {
                        const mainBox = $('.main-box');
                        const isFixed = mainBox.css('position') === 'fixed';
                        scrollHelper.scrollToTop(false, isFixed ? mainBox : undefined);
                    });
                }

                function updateLocationForIndex(index) {
                    loadRoute(`${reactRouterBridge.path()}?page=${index + 1}`);
                    scrollToContentTop();
                }

                // on scope for testing
                scope.gotoFormStep = index => {
                    scope.proxy.mobileNavExpanded = false;

                    if (getCurrentPage() - 1 === index) {
                        return;
                    }

                    updateLocationForIndex(index);
                };

                // This has to support the case of the user clicking the "Save and Next" button,
                // clicking one of the nav buttons, or clicking back on the browser.
                const removeReactRouterListener = reactRouterBridge.on('routeChangeStart', event => {
                    // if navigating away from currentForm, drop its errors
                    delete scope.schemaFieldErrors;
                    delete scope.schemaLanguageFieldErrors;

                    // NOTE: we check for dirtied forms beyond just `currentForm`
                    // because we have sub-components that might be registering forms for
                    // their own validation, etc - which could be dirtied as well
                    if (elem.find('form.ng-dirty').length > 0) {
                        const targetLocation = event.nextUrl;
                        event.preventDefault();

                        DialogModal.confirm({
                            text: translationHelper.get('unsaved_changes_confirm'),
                            confirmCallback() {
                                scope.careerProfile = getCareerProfileProxy();
                                // NOTE: this assigned `scope.programFamilyApplication` before, which no
                                // other line reads. So discarding unsaved changes never reset the
                                // program family form datum proxy.
                                scope.programFamilyFormDatum = getProgramFamilyFormDatumProxy();
                                scope.currentForm?.$setPristine(true);
                                loadRoute(targetLocation);
                            },
                        });
                    }
                });
                scope.$on('$destroy', () => {
                    removeReactRouterListener();
                });

                // HACK: in Facebook in-app browser, we need to guard against viewport bugs
                scope.$on('reactRouterBridgeChangeSuccess', () => {
                    FixViewportOnBlur.installBlurListenersOnLink(scope, elem);
                });
                FixViewportOnBlur.installBlurListenersOnLink(scope, elem);

                scope.$on('gotoFormStep', (_ev, index) => {
                    scope.gotoFormStep(index);
                });

                scope.$on('gotoEditProfileSection', (_ev, section) => {
                    const index = scope.steps.findIndex(step => step.stepName === section);
                    scope.gotoFormStep(index);
                });

                // set the initial page if provided directly
                if (scope.initialStep) {
                    reactRouterBridge.search({
                        page: scope.initialStep,
                    });
                }

                //-----------------------------
                // Form Submission
                //-----------------------------

                function showValidity() {
                    scope.currentForm.$setSubmitted(true);
                }

                // The validation errors the schema reports for the step on screen, split into the two
                // lists invalid-fields-links renders: `{ incomplete, wrongLanguage }`. Each list holds
                // `{ path, label }` entries.
                //
                // The two lists carry different messages, and the difference matters. An `incomplete`
                // field needs an answer. A `wrongLanguage` field already holds one, and the form asks
                // the user to write it in English instead. Telling them to "complete all required
                // fields" would be wrong, because the field is complete.
                //
                // Two schema paths can describe one visible field. `place_id` and `place_details` are
                // both set by the single location control on basic_info. `inputs` maps each path to the
                // marker it belongs to, so both collapse to one entry and the user does not see
                // "Location" twice. See `fieldPath` on FormConfig.
                function getSchemaFieldErrors() {
                    const formConfig = scope.steps?.find(step => step.stepName === scope.currentForm.$name);
                    if (!formConfig?.validationSchema) return { incomplete: [], wrongLanguage: [] };

                    const modelInput = buildFormCompletionInput(
                        scope.currentUser,
                        scope.steps,
                        scope.careerProfile,
                        scope.programFamilyFormDatum,
                    );

                    // The transient values win, because they describe what is on screen right now.
                    const input = {
                        ...modelInput,
                        formValues: { ...modelInput.formValues, ...scope.transientFormValues },
                    };

                    // The document order of the field markers. That is the reading order for these
                    // forms, and it needs no layout measurement.
                    const fieldOrder = [...elem[0].querySelectorAll('[data-field-path]')].map(node =>
                        node.getAttribute('data-field-path'),
                    );

                    // Key by the marker each path belongs to, so several failing paths behind one
                    // control produce one entry. The entry's path is the marker, which is what decides
                    // where it sorts and what clicking it scrolls to.
                    // A schema over an array reports an indexed path for each failing row, plus the bare
                    // array path for any rule that spans the rows. `work_experiences[0].job_title` and
                    // `work_experiences` both describe one section of the form, so they collapse to one
                    // entry named after the array. The user gets "Work Experience" rather than a row of
                    // cryptic indexed paths, and the entry scrolls to the section.
                    const incompleteByFieldPath = new Map();
                    const wrongLanguageByFieldPath = new Map();
                    getStepValidationErrors(formConfig, input).forEach(error => {
                        const rootPath = error.path.split('[')[0];
                        const fieldPath = formConfig.inputs?.[rootPath]?.fieldPath ?? rootPath;
                        const byFieldPath =
                            error.type === IS_NOT_MOSTLY_CJK_TEST ? wrongLanguageByFieldPath : incompleteByFieldPath;
                        if (byFieldPath.has(fieldPath)) return;
                        byFieldPath.set(fieldPath, {
                            path: fieldPath,
                            label: getSchemaFieldLabel(formConfig, rootPath),
                        });
                    });

                    // One field, one entry. A mostly-CJK answer that also breaks a length limit fails
                    // both rules, and naming it in both lists would read as a bug. The language entry
                    // wins, because the user has to rewrite the answer before the length means
                    // anything.
                    wrongLanguageByFieldPath.forEach((_entry, fieldPath) => incompleteByFieldPath.delete(fieldPath));

                    // A path with no marker sorts last, so a missing `data-field-path` degrades the
                    // order rather than dropping the error.
                    const rank = path => {
                        const index = fieldOrder.indexOf(path);
                        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
                    };
                    const sortByFieldOrder = byFieldPath =>
                        [...byFieldPath.values()].sort((a, b) => rank(a.path) - rank(b.path));

                    return {
                        incomplete: sortByFieldOrder(incompleteByFieldPath),
                        wrongLanguage: sortByFieldOrder(wrongLanguageByFieldPath),
                    };
                }

                // The human-readable label for a schema path, from the `inputs` metadata on the
                // FormConfig.
                //
                // `errorLabelKey` is a full locale key, not one relative to `careers.edit_career_profile`.
                // The labels on one step can come from more than one namespace: student_network_settings
                // takes its fun fact label from `careers.` and its privacy label from `student_network.`.
                //
                // A path with no key falls back to the path itself, which is ugly on purpose - a missing
                // label should be obvious in the UI rather than silently blank.
                function getSchemaFieldLabel(formConfig, path) {
                    const errorLabelKey = formConfig.inputs?.[path]?.errorLabelKey;
                    if (!errorLabelKey) return path;

                    // Pass the brand names, because several labels interpolate them. The templates do
                    // the same through `translate-values`. See the short answer labels on
                    // application_questions, which read "...why are you considering {{brandName}}".
                    //
                    // `programType` is here for the more_about_you `primary_reason_for_applying` label,
                    // the one key that interpolates it. Without it the placeholder renders empty. The
                    // value matches what more_about_you_form.html passes for the visible label.
                    return $translate.instant(errorLabelKey, {
                        brandName: scope.brandName,
                        brandNameShort: scope.brandNameShort,
                        programType: Cohort.standaloneShortProgramTitle(scope.programType),
                    });
                }

                // This used to be EditCareerProfileHelper.getCompletionPercentage, which chose the step
                // list by comparing the current path against '/settings/my-profile'. It lives here now
                // because this directive owns the form context and the unsaved proxies.
                //
                // We pass scope.careerProfile and scope.programFamilyFormDatum, which are proxies, so the
                // percentage reflects the values in the form rather than the values on the server.
                function getCompletionPercentage() {
                    if (scope.isStudentProfile && scope.careerProfile.completedAt) return 100;

                    return getFormsCompletePercent(
                        scope.steps,
                        buildFormCompletionInput(
                            scope.currentUser,
                            scope.steps,
                            scope.careerProfile,
                            scope.programFamilyFormDatum,
                        ),
                    );
                }

                scope.formActionsVisible = isLast => !isLast();

                scope.profileSubmitActionsVisible = isLast => scope.isStudentProfile && isLast();

                scope.handleSaveClick = () => {
                    if (scope.saveClickInterceptors[scope.currentStepName()]) {
                        scope.saveClickInterceptors[scope.currentStepName()]();
                    } else {
                        scope.save();
                    }
                };

                scope.save = () => scope.saveForm().catch(angular.noop);

                scope.saveForm = () => {
                    // The step's validationSchema decides whether the save proceeds. Angular still marks
                    // the form submitted, which is what gives each input its own error styling, but its
                    // validity no longer gates anything.
                    //
                    // That works because the schema sees the form's transient inputs as well as the
                    // models. See transientFormValues above.
                    //
                    // submit_application declares no schema, so it never blocks. It has no required
                    // inputs, so there is nothing for angular to have caught there either.
                    //
                    // demographics does have one: the written answer behind "other" for race. That is
                    // why it carries a schema even though every question on it is optional.
                    showValidity();
                    const schemaFieldErrors = getSchemaFieldErrors();
                    scope.schemaFieldErrors = schemaFieldErrors.incomplete;
                    scope.schemaLanguageFieldErrors = schemaFieldErrors.wrongLanguage;

                    if (scope.schemaFieldErrors.length > 0 || scope.schemaLanguageFieldErrors.length > 0) {
                        return $q.reject();
                    }

                    delete scope.schemaFieldErrors;
                    delete scope.schemaLanguageFieldErrors;

                    // see application_questions_form_dir.js
                    let shouldSaveForm = true;
                    scope.$broadcast('beforeSaveForm', _shouldSaveForm => {
                        shouldSaveForm = _shouldSaveForm;
                    });

                    if (!shouldSaveForm) {
                        return $q.reject();
                    }

                    // NOTE: mark the step visited before computing the percentage. A step with
                    // `requiresVisit` only counts as complete once it is visited.
                    markStepVisited(scope.currentForm.$name);

                    EditCareerProfileHelper.logEventForApplication(
                        scope.formContext,
                        `completed-${scope.currentForm.$name}`,
                    );

                    const completionPercentage = getCompletionPercentage();

                    const meta = {
                        // Transform this to snake_case for the server. We don't need to be destructive here, we can
                        // simply use a clone of scope.programFamilyFormDatum to send along in the meta
                        program_family_form_datum: transformKeyCase(scope.programFamilyFormDatum, {
                            to: 'snakeCase',
                        }),
                    };

                    if (scope.isStudentProfile && completionPercentage === 100) {
                        // Only update this in the context of /settings/my-profile, never in the context of submitting a
                        // ProgramApplication. We know that users will not complete their CareerProfile as part of submitting
                        // a ProgramApplication (they complete it via an enrollment sidebar todo) since the application does
                        // not include the same steps / forms as the profile. If we set this here in the context of the application,
                        // we could erroneously mark a user's CareerProfile as complete because they've filled out all required
                        // forms / field for the application.
                        meta.complete = true;
                    }

                    return scope.careerProfile.save(meta).then(() => {
                        // After the save is successful, we need to set the form back to a pristine state so the form's
                        // $dirty state also gets set to false. Otherwise, even if the save is successfully complete,
                        // the confirmation dialog modal may pop up if the user tries to navigate to a different section
                        // because the form's $dirty state wasn't reset.
                        scope.currentForm?.$setPristine();

                        if (scope.currentUser) {
                            // We still want scope.careerProfile to act as a proxy (see the $watch on currentUser above),
                            // so we reset scope.currentUser.career_profile to a COPY of scope.careerProfile, which ensures
                            // that scope.currentUser.career_profile isn't a reference to scope.careerProfile, thus maintaining
                            // it's behavior as a proxy.
                            scope.currentUser.career_profile = CareerProfile.new(scope.careerProfile.asJson());
                        }

                        // The parents and/or children may need to listen for when the profile has been saved.
                        // See settings_dir.js and submit_application_form_dir.js
                        // for where we listen to these events.
                        //
                        // NOTE: Order matters here (see submit_application_form_dir.js).
                        const savedCareerProfilePayload = { savedCompletionPercentage: completionPercentage };
                        scope.$broadcast('savedCareerProfile', savedCareerProfilePayload);
                        scope.$emit('savedCareerProfile', savedCareerProfilePayload);
                    });
                };

                scope.handleSaveAndNextClick = () => {
                    if (scope.saveNextClickInterceptors[scope.currentStepName()]) {
                        scope.saveNextClickInterceptors[scope.currentStepName()]();
                    } else {
                        scope.saveAndNext();
                    }
                };

                scope.saveAndNext = () => {
                    let currentFormWasIncompletePriorToSave;
                    if (scope.isStudentProfile) {
                        currentFormWasIncompletePriorToSave = scope.currentFormIsIncomplete();
                    }

                    scope
                        .saveForm()
                        .then(() => {
                            // When we're in the career profile context, if the user has any incomplete form steps,
                            // navigate them to the next incomplete form step from the current form step or back
                            // to the first incomplete form step if no incomplete form steps can be found after
                            // the current form step.
                            if (scope.isStudentProfile) {
                                const nextIncompleteStepIndex = scope.getNextIncompleteFormStepIndex();
                                if (nextIncompleteStepIndex > -1) {
                                    scope.gotoFormStep(nextIncompleteStepIndex);
                                    return;
                                }
                                if (currentFormWasIncompletePriorToSave) {
                                    scope.gotoFormStep(scope.steps.length - 1);
                                    return;
                                }
                            }

                            // Hopefully this is temporary until we can take https://github.com/quanticedu/back_royal/pull/15866.
                            // Previously, nextStepCallback was going through angular multi-step-form and performing navigation
                            // outside the context of reactRouterBridge.
                            const currentStep = getCurrentPage();
                            // If we're on the last page, there is no "next"
                            if (currentStep < scope.steps.length) {
                                // This is a little misleading because gotoFormStep(currentStep) just sounds like we're trying to goto the step
                                // we're already on, but gotoFormStep / updateLocationForIndex increment the index.
                                scope.gotoFormStep(currentStep);
                            }
                        })
                        .catch(angular.noop);
                };

                scope.currentFormIsIncomplete = () => {
                    if (!scope.stepsProgressMap) {
                        throw new Error(
                            'scope.stepsProgressMap must be defined to determine if the form was incomplete prior to save.',
                        );
                    }

                    const currentPage = getCurrentPage();
                    const currentStep = scope.steps[currentPage - 1];
                    return scope.stepsProgressMap[currentStep.stepName] === 'incomplete';
                };

                scope.getNextIncompleteFormStepIndex = () => {
                    if (!scope.stepsProgressMap) {
                        throw new Error(
                            'scope.stepsProgressMap must be defined to determine the next incomplete form step.',
                        );
                    }

                    const currentPage = getCurrentPage(); // 1-indexed

                    // If the user isn't on the last page, check if any of the form steps after
                    // the currentPage are incomplete and take them to the first one that we find.
                    for (let i = currentPage; i < scope.steps.length; i++) {
                        if (scope.stepsProgressMap[scope.steps[i].stepName] === 'incomplete') {
                            return i;
                        }
                    }

                    // Otherwise, check if any of the form steps before the currentPage are incomplete.
                    for (let i = 0; i < currentPage - 1; i++) {
                        if (scope.stepsProgressMap[scope.steps[i].stepName] === 'incomplete') {
                            return i;
                        }
                    }

                    return -1;
                };
            },
        };
    },
]);
